import { ComponentDataDestService } from '../component-data-dest.service';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { EventParticipantDTO } from '../../../dto/event/event-participant.dto';
import { Observable, catchError, shareReplay } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { ParticipateEventContextService } from '../../participate-event-context.service';
import { mapParticipateFormChoicesToStoredPreferences } from '../../../constants/duna-catering-preference-options';

function sanitizeGuestParticipateUsers(data: Record<string, unknown>): Record<string, unknown> {
  const users = data['users'];
  if (!Array.isArray(users)) {
    return data;
  }
  const filtered = users.filter((row) => {
    if (!row || typeof row !== 'object') {
      return false;
    }
    const entry = row as Record<string, unknown>;
    return (
      String(entry['firstName'] ?? '').trim() !== '' &&
      String(entry['lastName'] ?? '').trim() !== '' &&
      String(entry['email'] ?? '').trim() !== ''
    );
  });
  return { ...data, users: filtered };
}

export const join_event = (
  service: ComponentDataDestService,
  componentDataSourceService: ComponentDataSourceService,
  data: any
) => {
  const refreshEventParticipationDataSources = (request$: Observable<any>) => {
    const sharedRequest$ = request$.pipe(shareReplay(1));

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_participants').subscribe({
      error: (error) => console.error('Join event participants refresh error:', error)
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_instructors').subscribe({
      error: (error) => console.error('Join event instructors refresh error:', error)
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_waitlist').subscribe({
      error: (error) => console.error('Join event waitlist refresh error:', error)
    });

    componentDataSourceService.updateStoreData(sharedRequest$, 'event_cancellations').subscribe({
      error: (error) => console.error('Join event cancellations refresh error:', error)
    });
  };

  const router = service.injector.get(Router);
  const participateContext = service.injector.get(ParticipateEventContextService);
  const eventGuid = participateContext.resolveEventGuid(router);

  if (!eventGuid) {
    console.error('No valid event GUID found in URL');
    throw new Error('No valid event GUID found in URL');
  }

  const authService = service.injector.get(AuthService);
  const isLoggedIn = authService.isAuthenticated;

  if (data?.cancel_participation === true && isLoggedIn) {
    const currentUser = authService.getCurrentUser();
    const userUuid = currentUser?.uuid;
    if (!userUuid) {
      console.error('[join_event] cancel_participation true but no current user uuid');
      throw new Error('Bruger ikke fundet');
    }
    const url = `${environment.apiUrl}/events/${eventGuid}/participation`;
    const payload = {
      participant: {
        user: { uuid: userUuid },
        status: 'cancellation',
        role: 'attendee',
        data: {}
      }
    };
    const request$ = service.http.put<EventParticipantDTO>(url, payload).pipe(
      catchError((error) => {
        console.error('[join_event] Cancel participation error:', error);
        if (error?.error?.message) {
          console.error('[join_event] Server message:', error.error.message);
        }
        throw error;
      })
    );
    refreshEventParticipationDataSources(request$);
    return;
  }

  let participants: any[] = [];
  let selectedUserUuids: string[] = [];
  
  if (data) {
    if (data.add_event_users && Array.isArray(data.add_event_users)) {
      selectedUserUuids = data.add_event_users;
    } else if (data.add_event_users?.add_event_users && Array.isArray(data.add_event_users.add_event_users)) {
      selectedUserUuids = data.add_event_users.add_event_users;
    } else if (data.dualPanelSelector?.dualPanelSelector && Array.isArray(data.dualPanelSelector.dualPanelSelector)) {
      selectedUserUuids = data.dualPanelSelector.dualPanelSelector;
    } else if (data.dualPanelSelector && Array.isArray(data.dualPanelSelector)) {
      selectedUserUuids = data.dualPanelSelector;
    } else {
      Object.keys(data).forEach(key => {
        if ((key.includes('users') || key.includes('available')) && Array.isArray(data[key])) {
          selectedUserUuids = data[key];
        }
      });
    }
  }
  
  if (isLoggedIn) {
    const selfParticipate = data?.self_participate !== false && data?.self_participate !== 'false';
    const participantRole = data.participation_role || 'attendee';
    const cateringChoices = data.catering_choice;
    const additionalNotes = data.additional_notes;
    const participantData: any = {};
    
    const storedCateringPrefs =
      mapParticipateFormChoicesToStoredPreferences(cateringChoices);
    if (storedCateringPrefs.length > 0) {
      participantData.catering_preferences = storedCateringPrefs;
    }
    
    if (additionalNotes) {
      participantData.additional_notes = additionalNotes;
    }
    
    if (selfParticipate) {
      participants.push({
        user: {
          uuid: '',
          first_name: '',
          last_name: '',
          user: null,
          roles: null,
          organizations_id: null,
          locations: null,
          status: null
        },
        role: participantRole,
        status: 'pending',
        data: participantData
      });
    }
    
    if (selectedUserUuids.length > 0) {
      selectedUserUuids.forEach((userUuid: string, index: number) => {
        if (!userUuid || userUuid.trim() === '') {
          console.warn(`[join_event] WARNING: Empty or invalid UUID at index ${index}`);
        }
        participants.push({
          user: {
            uuid: userUuid,
            first_name: '',
            last_name: '',
            user: null,
            roles: null,
            organizations_id: null,
            locations: null,
            status: null
          },
          role: 'attendee',
          status: 'pending',
          data: {
            added_by_dual_panel: true
          }
        });
      });
    }

    if (participants.length === 0) {
      throw new Error('Ingen deltagere at tilføje. Vælg mindst én bruger eller deltag selv.');
    }
  } else {
    const url = `${environment.apiUrl}/events/${eventGuid}/join`;
    const payload =
      data && typeof data === 'object'
        ? sanitizeGuestParticipateUsers(data as Record<string, unknown>)
        : data;

    const request$ = service.http.post<EventParticipantDTO[]>(
      url,
      payload
    ).pipe(
      catchError(error => {
        console.error('Error in join event:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          url: error.url
        });
        if (error.error?.message) {
          console.error('Server error message:', error.error.message);
        }
        throw error;
      })
    );

    refreshEventParticipationDataSources(request$);
    return;
  }

  const url = `${environment.apiUrl}/events/${eventGuid}/join`;
  const payload = {participants: participants};

  const request$ = service.http.post<EventParticipantDTO[]>(
    url,
    payload
  ).pipe(
    catchError(error => {
      console.error('Error in join event:', error);
      console.error('Error details:', {
        status: error.status,
        statusText: error.statusText,
        error: error.error,
        url: error.url
      });
      if (error.error?.message) {
        console.error('Server error message:', error.error.message);
      }
      throw error;
    })
  );

  refreshEventParticipationDataSources(request$);
};
